import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import NumberFormat from 'react-number-format';
import OfferPaymentPlanComponent from './OfferPaymentPlanComponent';
import OfferPaymentPlanSummary from './OfferPaymentPlanSummary';
import EuroSymbol from '@mui/icons-material/EuroSymbol';
import CollapseOrBlankCard from 'components/common/CollapseOrBlankCard';
import WrappedFakeText from 'components/common/WrappedFakeText';

//eslint-disable-next-line react/display-name
export const getLabelValue = t => (value, valueType, format) => {
  switch (valueType) {
    case 'System.DateTime':
      return t('DATE', { date: value });
    case 'System.Decimal': {
      const scale = format?.includes('n4') ? 4 : 2;
      return <NumberFormat thousandSeparator={true} decimalScale={scale} displayType='text' value={value} />;
    }
    default:
      return value;
  }
};

export const OfferPaymentPlanCard = ({
  paymentPlan,
  paymentPlanSummary,
  currencyCode,
  importPaymentPlan,
  allowScheduleImport,
  showScheduleImportButton,
  onExpand,
  cardExpanded,
  isLoaded,
  exportPaymentPlan,
  fileInputRef,
  onImportScheduleClick,
  exportPaymentPlanWithTemplateOutput,
  generateInvoice,
  isInvoiceGenerationVisible,
  canGenerateInvoice,
  canDownloadInvoice,
  isDownloadingInvoice,
  downloadInvoice,
  cancelInvoice,
  canCancelInvoice,
  cancellingInvoice
}) => {
  const { t } = useTranslation();
  return (
    <CollapseOrBlankCard
      id={'offerPaymentPlanCard'}
      canExpand={!!paymentPlan.scheduleType}
      icon={EuroSymbol}
      title={t('OfferEdit.PaymentPlan.Title')}
      subheader={
        !paymentPlan.hidden &&
        isLoaded && (
          <OfferPaymentPlanSummary
            scheduleType={paymentPlan.scheduleType}
            paymentPlanSummary={paymentPlanSummary}
            currencyCode={currencyCode}
          />
        )
      }
      hideSubheaderOnExpand
      expanded={cardExpanded}
      onToggle={onExpand}
    >
      {!isLoaded && cardExpanded ? (
        <WrappedFakeText />
      ) : (
        <OfferPaymentPlanComponent
          paymentPlan={paymentPlan}
          getLabelValue={getLabelValue(t)}
          importPaymentPlan={importPaymentPlan}
          allowScheduleImport={allowScheduleImport}
          showScheduleImportButton={showScheduleImportButton}
          exportPaymentPlan={exportPaymentPlan}
          fileInputRef={fileInputRef}
          onImportScheduleClick={onImportScheduleClick}
          exportPaymentPlanWithTemplateOutput={exportPaymentPlanWithTemplateOutput}
          isInvoiceGenerationVisible={isInvoiceGenerationVisible}
          canGenerateInvoice={canGenerateInvoice}
          canDownloadInvoice={canDownloadInvoice}
          generateInvoice={generateInvoice}
          isDownloadingInvoice={isDownloadingInvoice}
          downloadInvoice={downloadInvoice}
          cancelInvoice={cancelInvoice}
          canCancelInvoice={canCancelInvoice}
          cancellingInvoice={cancellingInvoice}
        />
      )}
    </CollapseOrBlankCard>
  );
};

OfferPaymentPlanCard.displayName = 'OfferPaymentPlanCard';

OfferPaymentPlanCard.propTypes = {
  paymentPlan: PropTypes.object.isRequired,
  paymentPlanSummary: PropTypes.object,
  currencyCode: PropTypes.string.isRequired,
  importPaymentPlan: PropTypes.func.isRequired,
  allowScheduleImport: PropTypes.bool.isRequired,
  showScheduleImportButton: PropTypes.bool.isRequired,
  onExpand: PropTypes.func.isRequired,
  cardExpanded: PropTypes.bool,
  isLoaded: PropTypes.bool.isRequired,
  exportPaymentPlan: PropTypes.func.isRequired,
  fileInputRef: PropTypes.object,
  onImportScheduleClick: PropTypes.func.isRequired,
  exportPaymentPlanWithTemplateOutput: PropTypes.string,
  isInvoiceGenerationVisible: PropTypes.bool.isRequired,
  canGenerateInvoice: PropTypes.bool.isRequired,
  generateInvoice: PropTypes.func.isRequired,
  canDownloadInvoice: PropTypes.bool.isRequired,
  isDownloadingInvoice: PropTypes.bool.isRequired,
  downloadInvoice: PropTypes.func.isRequired,
  cancelInvoice: PropTypes.func.isRequired,
  canCancelInvoice: PropTypes.bool.isRequired,
  cancellingInvoice: PropTypes.bool.isRequired
};

export default OfferPaymentPlanCard;
